import { motion } from "framer-motion";
import { Dumbbell, Salad, HeartPulse } from "lucide-react";
import { useTheme } from "../../context/ThemeContext";

const features = [
  {
    icon: Dumbbell,
    title: "Precision Programming",
    description:
      "Strength-first training engineered for aesthetics — progressive overload, form mastery, and performance metrics.",
  },
  {
    icon: Salad,
    title: "Strategic Nutrition",
    description:
      "Practical structure you can follow. Macros, meal framework, and adherence — built for your lifestyle.",
  },
  {
    icon: HeartPulse,
    title: "Injury-Smart Progression",
    description:
      "Mobility + stability integrated so you progress without breaking down. Train hard — intelligently.",
  },
];

export default function SystemSection({ cta }) {
  const { theme } = useTheme();
  return (
    <section
      className="mt-24 relative py-5 md:py-5 bg-[var(--dvx-surface)] transition-colors duration-300 bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: "url('/images/method_bg.jpeg')" }}
    >
      {/* Overlay */}
      <div
        className={`absolute inset-0 transition-all duration-300 ${
          theme === "dark" ? "bg-black/60" : "bg-white/70"
        }`}
      ></div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* HEADER */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h3 className="text-[var(--dvx-primary)] font-oswald text-xl font-semibold uppercase tracking-[0.20.em]">
            DVX Method
          </h3>

          <h2 className="mt-4 font-oswald text-4xl font-bold text-[var(--dvx-text)] uppercase tracking-tight">
            Not a workout.{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--dvx-primary)] to-[var(--dvx-primary-dark)]">
              A system.
            </span>
          </h2>

          <p className="mt-4 text-[var(--dvx-text-muted)] font-manrope text-lg max-w-2xl mx-auto">
            Elite results require an elite process — execution, tracking, and
            accountability.
          </p>
        </motion.div>

        {/* CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.12 }}
                className="bg-[var(--dvx-bg)] border border-[var(--dvx-border)] p-8 flex flex-col gap-5 group hover:border-[var(--dvx-primary)]/50 transition-colors duration-300"
              >
                <div className="w-12 h-12 bg-[var(--dvx-primary)]/10 flex items-center justify-center">
                  <Icon size={22} className="text-[var(--dvx-primary)]" />
                </div>

                <div className="flex-1">
                  <h3 className="font-oswald text-xl font-bold text-[var(--dvx-text)] uppercase tracking-wide mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-[var(--dvx-text-muted)] font-manrope text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </div>

                <div className="w-10 h-0.5 bg-gradient-to-r from-[var(--dvx-primary)] to-[var(--dvx-primary-dark)] group-hover:w-16 transition-all duration-300" />
              </motion.div>
            );
          })}
        </div>

        {/* CTA STRIP */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 border border-[var(--dvx-border)] bg-[var(--dvx-bg)] p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
        >
          {/* TEXT */}
          <div>
            <h3 className="font-oswald text-lg font-bold uppercase text-[var(--dvx-text)] mb-2">
              Built for serious clients.
            </h3>
            <p className="text-sm text-[var(--dvx-text-muted)] max-w-xl">
              Wedding/event timeline, lifestyle constraints, injuries, plateaus
              — DVX adapts the system to you.
            </p>
          </div>

          {/* BUTTONS */}
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            {cta?.callNow && (
              <a
                href={`tel:${cta.callNow}`}
                className="text-center px-5 py-2 border border-[var(--dvx-border)] text-sm font-semibold hover:bg-[var(--dvx-primary)] hover:text-white transition"
              >
                Call Now
              </a>
            )}

            <a
              href={cta?.bookLink || "/contact"}
              className="text-center px-5 py-2 bg-[var(--dvx-primary)] text-white text-sm font-semibold hover:opacity-90 transition"
            >
              Book Assessment
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
